// ── The driver's case card, as the driver sees it ────────────────────────────
// Lifted out of DriverLogPage on 1 Sep 2026 so the office can look at it too: the Customer
// Cases page's "Driver Portal View" renders THIS component, with `preview` on, over the
// same data the portal is served (customer-cases-admin-sb → driverPreview →
// _shared/case-driver-view, the portal's own builder). A preview that was a second,
// office-side drawing of the driver's screen would start telling the truth and end up
// telling a story — the two would drift the first time either side changed. So there is
// one card, and the office looks at the real one.
//
// `preview` makes it read-only: every action still renders exactly as the driver sees it,
// and none of them post. Nothing in the office's preview can answer a case for a driver.
import React, { useState, useEffect, useMemo } from 'react';
import {
  AlertTriangle, Calendar, Camera, CheckCircle, ChevronDown, ChevronUp, Clock,
  HelpCircle, MapPin, MessageSquare, Package, Search, Send,
} from 'lucide-react';
import CameraCapture from './CameraCapture';
import CaseQuestionnaire from './CaseQuestionnaire';
import { pendingQuestions } from '../utils/caseQuestionnaire';
import { scanState, toDate } from '../utils/caseScan';
import { caseDueMeta } from '../utils/caseDue';

// "14:22:11" | "14:22" → "2:22 pm" — plain time for the driver-facing scan snapshot.
const ampm = (t) => {
  const m = /^(\d{1,2}):(\d{2})/.exec((t || '').toString());
  if (!m) return t || '';
  let h = +m[1]; const suf = h >= 12 ? 'pm' : 'am'; h = h % 12 || 12;
  return `${h}:${m[2]} ${suf}`;
};
// "2026-08-05" → "Tue 5 Aug" — the scan date for the driver (parsed as calendar parts,
// no timezone shift). Critical for the driver to place the delivery.
const fmtDay = (iso) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec((iso || '').toString());
  if (!m) return '';
  const dow = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][new Date(Date.UTC(+m[1], +m[2] - 1, +m[3])).getUTCDay()];
  const mon = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][+m[2] - 1];
  return `${dow} ${+m[3]} ${mon}`;
};


// ── Customer cases (AP customer enquiries pushed to the driver) ──────────────
// A photo may go on ANY case answer — a driver shouldn't have to wait to be asked
// before showing what they saw (Paul, 25 Aug 2026). When the office HAS asked, it opens
// expanded under the gold heading; otherwise it waits behind one line so it doesn't
// crowd the answer box.
//
// More than one (Paul, 27 Oct 2026): the parcel, the door it was left at and the street
// number are three photos, and one slot made the driver choose. Each capture is added to
// the list; the camera stays underneath for the next until the cap. The cap is the 6 MB
// function body — every photo rides the same POST as the answer.
const CASE_PHOTO_MAX = 5;
const CASE_PHOTO_BYTES = 4.5 * 1024 * 1024; // base64 characters, all photos together
function CasePhotoField({ requested, photos, onChange, bare = false }) {
  const list = photos || [];
  const [open, setOpen] = useState(bare || !!requested || list.length > 0);
  useEffect(() => { if (requested || list.length) setOpen(true); }, [requested, list.length]);
  const used = list.reduce((n, u) => n + u.length, 0);
  const full = list.length >= CASE_PHOTO_MAX || used >= CASE_PHOTO_BYTES;
  if (!open) {
    return (
      <button type="button" onClick={() => setOpen(true)}
        style={{ alignSelf: 'flex-start', display: 'inline-flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', padding: 0, color: 'var(--hp-dim)', fontSize: '0.8rem', textDecoration: 'underline', cursor: 'pointer' }}>
        <Camera size={13} /> Add photos
      </button>
    );
  }
  return (
    <div>
      {!bare && (
        <div style={{ fontSize: '0.72rem', fontWeight: 800, color: requested ? 'var(--hp-gold)' : 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'flex', alignItems: 'center', gap: 5, marginBottom: 6 }}>
          <Camera size={13} /> {requested ? 'The office asked you to add a photo' : 'Add photos (optional)'}
        </div>
      )}
      {list.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
          {list.map((u, i) => (
            <div key={i} style={{ position: 'relative', width: 88, height: 88 }}>
              <img src={u} alt={`Photo ${i + 1}`} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 8, border: '1px solid var(--hp-border)', display: 'block' }} />
              <button type="button" onClick={() => onChange(list.filter((_, j) => j !== i))} aria-label={`Remove photo ${i + 1}`}
                style={{ position: 'absolute', top: 3, right: 3, width: 24, height: 24, borderRadius: 12, border: 'none', background: 'rgba(0,0,0,0.7)', color: '#fff', fontSize: 14, lineHeight: '24px', padding: 0, cursor: 'pointer' }}>×</button>
            </div>
          ))}
        </div>
      )}
      {full ? (
        <div style={{ fontSize: '0.76rem', color: 'var(--hp-dim)' }}>
          That&rsquo;s as many as one answer can carry. Send this one, then add more in an update.
        </div>
      ) : (
        <>
          {list.length > 0 && <div style={{ fontSize: '0.74rem', color: 'var(--hp-dim)', marginBottom: 4 }}>Add another:</div>}
          <CameraCapture nativeFirst guide="document" maxEdge={1600} quality={0.8} value={null}
            onCapture={(u) => u && onChange([...list, u])} onClear={() => {}} />
        </>
      )}
    </div>
  );
}

// A driver works each open case confirmed to them: a first-pass answer, a photo,
// the office↔driver thread, "I'll re-attempt tomorrow" commitments that carry over,
// and the next-morning "did you do it?" confirmation. Shown on the shift-start
// screen, again before finishing, and at Onboard. Nothing goes to AP automatically.
const CC_API = '/.netlify/functions/portal-customer-cases';
const ccApi = (action, payload) => fetch(CC_API, {
  method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action, ...payload }),
}).then(r => r.json());


// What a button press does in the office's preview. Said in the card's own error line so
// it lands where the tap did, rather than as a silent no-op that reads as a broken portal.
// Tell the office the driver has this card open. Not in the office's preview, and at most once
// per card per opening — the server ignores anyone who isn't the case's driver or recovery.
function useSeen(id, open, preview) {
  useEffect(() => {
    if (open && !preview && id) ccApi('seen', { id }).catch(() => {});
  }, [id, open, preview]);
}

const PREVIEW_NOTE = 'Preview only — nothing is sent. This is the driver\u2019s screen, not yours to answer on.';

// Whether the driver is actually looking at this case, said the same way wherever it is
// asked — the office's Driver Portal View and a team leader's "see what my driver sees"
// are the same question about the same case, and two answers to it would mean one of them
// is wrong. The verdict comes from the server (_shared/case-driver-view visibilityOf);
// this is only its words. Each side colours it in its own palette.
function previewVerdict(v, driver) {
  const who = driver || 'the driver';
  if (!v) return { state: 'nodriver', text: 'Could not work out who is looking at this case.' };
  if (v.state === 'live') return { state: 'live', text: `On ${who}'s portal now — the Customer Service tab, exactly as below.` };
  if (v.state === 'history') return { state: 'history', text: `Case ${v.status} — this sits in ${who}'s history now, not their to-do list. It reads as below, with nothing left to answer.` };
  // Said without naming who fixes it: the office reads this on the case drawer, a team
  // leader reads the same sentence on their phone, and only one of them can confirm a driver.
  if (v.state === 'unmatched') return { state: 'unmatched', text: `${who} can see this, but no driver is confirmed on the case — the portal refuses every answer until one is.` };
  return {
    state: 'nodriver',
    text: v.open
      ? 'No driver is confirmed, so nobody is looking at this. Below is what they will see once one is.'
      : `No driver was ever confirmed, and the case is ${v.status} — nobody saw this. Below is what it would have looked like.`,
  };
}

// ── Standing delivery requests ───────────────────────────────────────────────
// What a customer has asked us to do at their address from now on. It arrives out of a case,
// but it is NOT the case: the case gets answered and closed, and this stays. The one that
// caused this to exist was pushed and resolved the same day, so it went straight into the
// driver's Resolved list — a list nobody opens — and the customer's request was never read
// (Paul, 1 Sep 2026).
//
// Two things have to be true: they must actively say they have it (the office keeps the date),
// and it must still be there in a month when they are standing at the door trying to remember
// what the sign said.
function DeliveryRequestCard({ r, onAcked, preview }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const acked = !!r.acked_at;

  const ack = async () => {
    // The office reading this through Driver Portal View sees the button the driver sees, and
    // must not be able to press it for them — an acknowledgement is a record of what THEY did.
    if (preview) { setErr(PREVIEW_NOTE); return; }
    setBusy(true); setErr('');
    try {
      const res = await ccApi('ackRequest', { id: r.id });
      if (res && res.success) onAcked && onAcked(r.id, res.acked_at);
      else setErr((res && res.error) || 'Something went wrong');
    } catch { setErr('Network error — try again'); }
    finally { setBusy(false); }
  };

  return (
    <div style={{ background: 'var(--hp-surface)', border: `${acked ? 1 : 2}px solid ${acked ? 'var(--hp-border)' : '#ef4444'}`, borderRadius: 'var(--radius)', padding: 13, marginBottom: 8 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap', marginBottom: 7 }}>
        <span style={{ fontSize: '0.66rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.04em', padding: '2px 7px', borderRadius: 5, border: '1px solid var(--hp-gold)', color: 'var(--hp-gold)' }}>Customer request</span>
        {acked
          ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: '0.7rem', color: '#34d399' }}><CheckCircle size={12} /> you have this</span>
          : <span style={{ fontSize: '0.7rem', fontWeight: 800, color: '#ef4444', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Please read</span>}
      </div>
      <div style={{ fontSize: '0.95rem', fontWeight: 800, display: 'flex', gap: 6, alignItems: 'flex-start' }}>
        <MapPin size={15} style={{ color: 'var(--hp-gold)', flexShrink: 0, marginTop: 2 }} />
        <span>{r.address}</span>
      </div>
      <div style={{ fontSize: '0.9rem', marginTop: 8, whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>{r.request}</div>
      {(r.photos || []).length > 0 && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 9 }}>
          {r.photos.map((src, i) => (
            <a key={i} href={src} target="_blank" rel="noreferrer"><img src={src} alt="the customer's request" style={{ width: 120, height: 120, objectFit: 'cover', borderRadius: 8, border: '1px solid var(--hp-border)' }} /></a>
          ))}
        </div>
      )}
      <div style={{ fontSize: '0.72rem', color: 'var(--hp-dim)', marginTop: 8 }}>
        {r.case_number ? `From case ${r.case_number} · ` : ''}
        {acked
          ? `You confirmed this on ${new Date(r.acked_at).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}. It stays here for whenever you need it.`
          : 'This one stays on your phone — you can come back to it any time under Customer Service → Requests.'}
      </div>
      {!acked && (
        <button disabled={busy} onClick={ack}
          style={{ width: '100%', marginTop: 11, padding: 13, borderRadius: 8, border: 'none', background: 'var(--hp-green)', color: '#04110b', fontWeight: 800, fontSize: '0.92rem', cursor: 'pointer' }}>
          <CheckCircle size={16} /> Got it — I&rsquo;ll do this from now on
        </button>
      )}
      {err && <div style={{ color: '#ef4444', fontSize: '0.8rem', marginTop: 6 }}>{err}</div>}
    </div>
  );
}

// ── Customer case cards (shift-start / finish / onboard) ─────────────────────
const CC_LABELS = {
  wrong_address: 'Wrong address', no_attempt: 'No delivery attempt', not_received: 'Not received',
  signature: 'Signature query', damaged: 'Damaged', safe_drop: 'Safe drop', conduct: 'Complaint',
  redelivery: 'Redelivery', rts: 'Return to sender', other: 'Customer enquiry',
};
// The case's SLA countdown — counted in WORKING days, so a weekend or a public holiday
// never reads as the driver running late. Shared with the office triage list.
const ccDue = caseDueMeta;
const ccPhotoParam = (dataUrls) => (Array.isArray(dataUrls) ? dataUrls : dataUrls ? [dataUrls] : [])
  .filter(Boolean)
  .map((u) => ({ base64: u.split(',')[1], contentType: (u.match(/^data:(.*?);/) || [])[1] || 'image/jpeg' }));

// Guided template answers, tailored to the case type. An option may carry a `followUp`
// (a second question with its own options) — e.g. a delivered case asks "who signed?".
// Paul can reword these freely. The universal "Unsure — I'll investigate" flow is handled
// in the card itself (it sets an expected response time instead of an answer).
function ccTemplates(c) {
  const cat = c.category;
  const sig = c.signature_name;
  if (['not_received', 'safe_drop', 'signature'].includes(cat)) {
    const opts = [];
    if (sig) opts.push({ label: `I delivered here`, followUp: { question: `Who is ${sig}?`, options: ['Reception / front desk', 'The customer', 'A family member / housemate', 'An office worker', 'Concierge / building manager', 'A neighbour', 'I don’t recognise the name'] } });
    return opts.concat([
      { label: 'Left in a safe place (photo on file)' },
      { label: 'Handed to the customer in person' },
      { label: 'Delivered to reception / concierge' },
      { label: 'I don’t recall this delivery' },
    ]);
  }
  // A sender recall asks the driver for one thing only: has the parcel gone yet, and if not,
  // bring it back. Answering it out of the re-attempt list ("will re-attempt") would be the
  // wrong promise entirely.
  if (cat === 'rts') {
    return [
      { label: 'Still on the van — bringing it back for RTS' },
      { label: 'Already delivered — sorry' },
      { label: 'Not on the van / can’t find it' },
      { label: 'Not one of my parcels' },
    ];
  }
  if (['no_attempt', 'wrong_address', 'redelivery'].includes(cat)) {
    return [
      { label: 'Attempted — no answer, carded' },
      { label: 'Address label was unclear / wrong' },
      { label: 'Couldn’t find the unit / no access' },
      { label: 'Went to the wrong spot — will re-attempt' },
      { label: 'Not one of my parcels' },
    ];
  }
  if (cat === 'conduct') return [{ label: 'I recall this — I’ll explain below' }, { label: 'I don’t recall any issue' }];
  if (cat === 'damaged') return [{ label: 'It was fine when I delivered it' }, { label: 'I noticed damage — I’ll explain below' }];
  return [];
}
// Expected-response date for the "I'll investigate" flow → yyyy-mm-dd (Sydney).
function ccExpectedDate(kind) {
  const d = new Date();
  if (kind === 'next') d.setDate(d.getDate() + 1);
  else if (kind === 'two') d.setDate(d.getDate() + 2);
  return d.toLocaleDateString('en-CA', { timeZone: 'Australia/Sydney' });
}
// A short guided questionnaire for delivery-dispute cases (safe drop / not received /
// signature). Returns ordered Yes/No steps; a chosen option may reveal a text prompt.
// null → the case uses the simpler quick-answer chips instead.
function ccFlow(c) {
  if (!['safe_drop', 'not_received', 'signature'].includes(c.category)) return null;
  const hasImg = (c.images || []).length > 0;
  return [
    { id: 'recall', q: 'Do you recall this delivery?', opts: [{ k: 'yes', label: 'Yes' }, { k: 'no', label: 'No' }] },
    { id: 'address', q: hasImg ? 'Is the photo at the correct address?' : 'Was it delivered to the correct address?',
      opts: [{ k: 'yes', label: 'Yes — correct' }, { k: 'no', label: 'No', prompt: hasImg ? 'Where was the photo taken / where did you actually leave it?' : 'Where was it actually left?' }] },
    { id: 'more', q: 'Anything else about this delivery you can share?',
      opts: [{ k: 'yes', label: 'Yes', prompt: 'Please share the details' }, { k: 'no', label: 'No' }] },
  ];
}

// The scanner-audit snapshot the office pushed onto a case — the disputed drop plus the
// deliveries either side of it on that run. Shared by the driver's own case card and by the
// recovery card, where a helper searching the address needs exactly the same picture.
// A parcel that was on more than one run arrives with `days` — one panel per day, oldest first.
function ScanSnapshotPanel({ snap, title, footer }) {
  if (!snap) return null;
  const days = Array.isArray(snap.days) && snap.days.length > 1 ? snap.days : [snap];
  const shown = days.filter((d) => d && Array.isArray(d.neighbours) && d.neighbours.length);
  if (!shown.length) return null;
  if (shown.length === 1) return <ScanDayPanel snap={shown[0]} title={title} footer={footer} />;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ fontSize: '0.8rem', color: 'var(--hp-dim)' }}>This parcel was out for delivery on {shown.length} different days — each one is below.</div>
      {shown.map((d, i) => (
        <ScanDayPanel key={i} snap={d} title={`${title} · day ${i + 1} of ${shown.length}`} footer={i === shown.length - 1 ? footer : null} />
      ))}
    </div>
  );
}
function ScanDayPanel({ snap, title, footer }) {
  return (
    <div style={{ border: '1px solid rgba(245,158,11,0.4)', borderRadius: 10, overflow: 'hidden' }}>
      <div style={{ padding: '9px 12px', background: 'rgba(245,158,11,0.1)' }}>
        <div style={{ fontSize: '0.78rem', fontWeight: 800, color: '#f59e0b', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'flex', alignItems: 'center', gap: 6 }}>
          <Package size={13} /> {title}
        </div>
        {snap.date && <div style={{ fontSize: '0.82rem', fontWeight: 700, marginTop: 3 }}>{fmtDay(snap.date)}</div>}
      </div>
      <div style={{ padding: '4px 0' }}>
        {snap.neighbours.map((n, i) => (
          n.isTarget ? (
            <div key={i} style={{ margin: '14px 10px', padding: '13px 15px', borderRadius: 12, border: '2px solid #f59e0b', background: 'rgba(245,158,11,0.10)', boxShadow: '0 6px 0 #d9840b, 0 10px 18px rgba(217,132,11,0.30)' }}>
              <div style={{ display: 'inline-block', fontSize: '0.62rem', fontWeight: 800, letterSpacing: '0.06em', color: '#fff', background: '#f59e0b', padding: '3px 9px', borderRadius: 6, marginBottom: 8, textTransform: 'uppercase' }}>The one we’re asking about</div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 10 }}>
                <span style={{ fontSize: '1.15rem', fontWeight: 800, lineHeight: 1.25 }}>{n.address || '—'}</span>
                <span style={{ fontSize: '0.9rem', fontWeight: 800, color: '#f59e0b', whiteSpace: 'nowrap', fontFamily: 'var(--font-mono)' }}>{ampm(n.time)}</span>
              </div>
              <div style={{ fontSize: '0.8rem', color: '#f59e0b', marginTop: 6 }}>
                {snap.date ? `${fmtDay(snap.date)} · ` : ''}{/safe place/i.test(n.event) ? 'Left in a safe place · no signature' : (n.signatory ? `signed ${n.signatory}` : n.event)}
              </div>
            </div>
          ) : (
            <div key={i} style={{ display: 'flex', gap: 10, padding: '8px 12px', alignItems: 'flex-start', borderTop: i ? '1px solid var(--hp-border)' : 'none' }}>
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: '0.8rem', color: 'var(--hp-dim)', whiteSpace: 'nowrap', minWidth: 58 }}>{ampm(n.time)}</span>
              <span style={{ flex: 1, fontSize: '0.86rem' }}>
                <div style={{ fontWeight: 500 }}>{n.address || '—'}</div>
                <div style={{ fontSize: '0.75rem', color: 'var(--hp-dim)' }}>
                  {/safe place/i.test(n.event) ? 'left in a safe place' : (n.signatory ? `signed ${n.signatory}` : n.event)}
                </div>
              </span>
            </div>
          )
        ))}
      </div>
      {footer && (
        <div style={{ padding: '8px 12px', fontSize: '0.78rem', color: 'var(--hp-dim)', borderTop: '1px solid var(--hp-border)' }}>{footer}</div>
      )}
    </div>
  );
}

// Where a recovery driver should knock FIRST. The audit snapshot is the disputed scan plus
// the drops either side of it, so a parcel that isn't at its own address is nearly always at
// one of those buildings — most tellingly the one that took the same signature at the same
// minute (a mailroom or concierge signing for the whole run). Groups the neighbours by
// building (street number + street), ranks by same-signature then count, and returns the top
// two. Returns [] if the addresses don't parse — never guesses.
const buildingOf = (addr) => {
  const s = (addr || '').toString().replace(/\s+/g, ' ').trim().toUpperCase();
  const re = /(\d+)\s+([A-Z][A-Z' ]*?)\s+(ST|STREET|RD|ROAD|AVE|AVENUE|PDE|PARADE|LN|LANE|PL|PLACE|DR|DRIVE|HWY|HIGHWAY|CRES|CRESCENT|TCE|TERRACE|SQ|SQUARE)\b/g;
  let m, last = null;
  while ((m = re.exec(s))) last = m;
  if (!last) return '';
  // "591 George St" and "591 GEORGE STREET" are the same building — collapse the suffix, or
  // the drops at one address split into two weaker-looking groups.
  const SUFFIX = { STREET: 'ST', ROAD: 'RD', AVENUE: 'AVE', PARADE: 'PDE', LANE: 'LN', PLACE: 'PL', DRIVE: 'DR', HIGHWAY: 'HWY', CRESCENT: 'CRES', TERRACE: 'TCE', SQUARE: 'SQ' };
  return `${last[1]} ${last[2].trim()} ${SUFFIX[last[3]] || last[3]}`;
};
function likelyBuildings(snap) {
  if (!snap || !Array.isArray(snap.neighbours)) return [];
  const target = snap.neighbours.find((n) => n.isTarget) || snap.target || {};
  const home = buildingOf(target.address);
  const sig = (target.signatory || '').toString().trim().toUpperCase();
  const groups = {};
  snap.neighbours.filter((n) => !n.isTarget).forEach((n) => {
    const b = buildingOf(n.address);
    if (!b || b === home) return;
    const g = groups[b] || (groups[b] = { building: b, count: 0, sameSig: 0, signatory: '' });
    g.count += 1;
    if (sig && (n.signatory || '').toString().trim().toUpperCase() === sig) { g.sameSig += 1; g.signatory = sig; }
  });
  return Object.values(groups).sort((a, b) => (b.sameSig - a.sameSig) || (b.count - a.count)).slice(0, 2);
}

// A photo the office has put on this case for the driver, with the question they are asking
// about it. Its own panel, deliberately not mixed in with AP's delivery photo above: that one
// is evidence from the day, this one is somebody asking you something now.
function OfficePhotoPanel({ photos, forHelper }) {
  if (!photos || !photos.length) return null;
  return (
    <div style={{ border: '2px solid var(--hp-gold)', borderRadius: 8, overflow: 'hidden' }}>
      <div style={{ padding: '10px 12px', background: 'rgba(201,168,76,0.12)' }}>
        <div style={{ fontSize: '0.72rem', fontWeight: 800, color: 'var(--hp-gold)', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'flex', alignItems: 'center', gap: 5 }}>
          <Camera size={13} /> {photos.length > 1 ? 'New photos from the office' : 'New photo from the office'}
        </div>
      </div>
      <div style={{ padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 12 }}>
        {photos.map((p) => (
          <div key={p.id}>
            {p.caption && <div style={{ fontSize: '0.92rem', fontWeight: 700, marginBottom: 7 }}>{p.caption}</div>}
            <a href={p.url} target="_blank" rel="noreferrer">
              <img src={p.url} alt="from the office" style={{ width: '100%', maxWidth: 320, borderRadius: 8, border: '1px solid var(--hp-border)', display: 'block' }} />
            </a>
          </div>
        ))}
        <div style={{ fontSize: '0.78rem', color: 'var(--hp-dim)' }}>
          Tap to enlarge. {forHelper
            ? 'Anything you notice, put it in the box below.'
            : 'Do you remember this drop — anything unusual about it? Tell us below, even if you are not sure.'}
        </div>
      </div>
    </div>
  );
}

// A RECOVERY the office has asked of this driver on someone ELSE'S case — the named driver
// is on leave / off the round and can't get to the address. Deliberately a separate, much
// simpler card: the helper reports what they found, they never answer for the delivery
// (no questionnaire, no commitment, no fault) — that stays with the driver who owns the case.
function AssistCaseCard({ c, onChanged, preview }) {
  const [open, setOpen] = useState(true);
  useSeen(c.id, open, preview);
  const [found, setFound] = useState('');       // 'found' | 'not-found' | ''
  const [note, setNote] = useState('');
  const [photos, setPhotos] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const a = c.assist || {};
  // The audit snapshot the office pushed onto the case is the single best search aid we
  // have — it shows where the driver actually was when the parcel was scanned.
  const likely = useMemo(() => likelyBuildings(c.scan_snapshot), [c.scan_snapshot]);
  const snapSig = ((c.scan_snapshot && c.scan_snapshot.target && c.scan_snapshot.target.signatory) || '').trim();

  const run = async (payload) => {
    // Office preview: the buttons are here because the driver sees them, and pressing one
    // must not answer a case in their name.
    if (preview) { setErr(PREVIEW_NOTE); return; }
    setBusy(true); setErr('');
    try {
      const r = await ccApi('assistReport', { id: c.id, ...payload, photos: ccPhotoParam(photos) });
      if (r && r.success) { setNote(''); setPhotos([]); setFound(''); onChanged && onChanged(); }
      else setErr((r && r.error) || 'Something went wrong');
    } catch { setErr('Network error — try again'); }
    finally { setBusy(false); }
  };
  const OUTCOMES = [
    { k: 'found', label: 'Found the parcel' },
    { k: 'left', label: 'Parcel is there — left with reception/customer' },
    { k: 'not-found', label: 'Not there — nothing found' },
    { k: 'no-access', label: 'Couldn’t get access' },
  ];
  const chip = { fontSize: '0.66rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.04em', padding: '2px 7px', borderRadius: 5, border: '1px solid' };
  const outcomeLabel = (OUTCOMES.find((o) => o.k === found) || {}).label || '';
  // The office can now write to the helper mid-search — "try the concierge at 591 George" —
  // and their phone is alerted for it. When they open the card, the same fact has to be
  // visible here, or the alert lands on a screen that looks unchanged.
  const msgs = (c.messages || []).filter((m) => !(m.who === 'office' && m.kind === 'admin'));
  const officeWaiting = msgs.length > 0 && msgs[msgs.length - 1].who === 'office';

  return (
    <div style={{ background: 'var(--hp-surface)', border: '2px solid #3b82f6', borderRadius: 'var(--radius)', padding: 13, marginBottom: 8 }}>
      <div onClick={() => setOpen((o) => !o)} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
        <span style={{ ...chip, color: '#3b82f6', borderColor: '#3b82f6' }}>Recovery</span>
        <span style={{ fontSize: '0.72rem', color: 'var(--hp-dim)' }}>for {a.for_driver || 'another driver'}</span>
        {officeWaiting && <span style={{ ...chip, color: 'var(--hp-gold)', borderColor: 'var(--hp-gold)' }}>Office wrote to you</span>}
        <span style={{ marginLeft: 'auto', color: 'var(--hp-dim)' }}>{open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}</span>
      </div>
      <div style={{ fontSize: '0.95rem', fontWeight: 800, marginTop: 8, display: 'flex', alignItems: 'flex-start', gap: 6 }}>
        <MapPin size={15} style={{ color: 'var(--hp-gold)', flexShrink: 0, marginTop: 2 }} />
        <span>{a.address || c.address || 'Address on the case'}</span>
      </div>
      {a.tracking_no && <div style={{ fontSize: '0.76rem', color: 'var(--hp-dim)', fontFamily: 'var(--font-mono)', marginTop: 3 }}>Article {a.tracking_no}</div>}

      {open && (
        <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ border: '1px solid rgba(59,130,246,0.5)', background: 'rgba(59,130,246,0.08)', borderRadius: 8, padding: '10px 12px', fontSize: '0.86rem', whiteSpace: 'pre-wrap' }}>
            <div style={{ fontSize: '0.7rem', fontWeight: 800, color: '#3b82f6', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 5 }}>What the office is asking</div>
            {a.task || 'Please search for this parcel at the address and report back.'}
          </div>

          {/* The office's note. Nothing appears here unless someone in the office chose to put
              it here during triage — AP's raw customer wording is no longer piped straight
              through, because it regularly confused the driver more than it helped. */}
          {c.driver_note && (
            <div style={{ border: '1px solid rgba(212,175,55,0.45)', background: 'rgba(212,175,55,0.07)', borderRadius: 8, padding: '10px 12px' }}>
              <div style={{ fontSize: '0.68rem', fontWeight: 800, color: 'var(--hp-gold)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 5 }}>Note from the office</div>
              <div style={{ fontSize: '0.84rem', whiteSpace: 'pre-wrap' }}>{c.driver_note}</div>
            </div>
          )}
          {!c.driver_note && c.summary && (
            <div style={{ fontSize: '0.82rem', color: 'var(--hp-dim)' }}>{c.summary}</div>
          )}

          <OfficePhotoPanel photos={c.driver_photos} forHelper />

          {(c.images || []).length > 0 && (
            <div>
              <div style={{ fontSize: '0.72rem', fontWeight: 800, color: 'var(--hp-gold)', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'flex', alignItems: 'center', gap: 5, marginBottom: 6 }}><Camera size={13} /> Photo taken at the original delivery</div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {c.images.map((src, i) => (
                  <a key={i} href={src} target="_blank" rel="noreferrer"><img src={src} alt="delivery" style={{ width: 130, height: 130, objectFit: 'cover', borderRadius: 8, border: '1px solid var(--hp-border)' }} /></a>
                ))}
              </div>
              <div style={{ fontSize: '0.74rem', color: 'var(--hp-dim)', marginTop: 4 }}>Tap to enlarge — this may show you where to look.</div>
            </div>
          )}

          {likely.length > 0 && (
            <div style={{ border: '2px solid #f59e0b', borderRadius: 10, background: 'rgba(245,158,11,0.10)', padding: '11px 13px' }}>
              <div style={{ fontSize: '0.72rem', fontWeight: 800, color: '#f59e0b', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'flex', alignItems: 'center', gap: 5, marginBottom: 7 }}>
                <Search size={13} /> Look here first
              </div>
              {likely.map((g) => (
                <div key={g.building} style={{ marginBottom: 6 }}>
                  <div style={{ fontSize: '1.02rem', fontWeight: 800, lineHeight: 1.25 }}>{g.building}</div>
                  <div style={{ fontSize: '0.79rem', color: 'var(--hp-dim)' }}>
                    {g.count} of {a.for_driver || 'the driver'}&rsquo;s drops in the same run
                    {g.sameSig > 0 && g.signatory ? ` · ${g.sameSig} signed ${g.signatory} — the same signature captured on our parcel` : ''}
                  </div>
                </div>
              ))}
              <div style={{ fontSize: '0.76rem', color: 'var(--hp-text)', marginTop: 6 }}>
                {snapSig
                  ? `Our parcel was signed “${snapSig}”. Ask there first — a mailroom or concierge signing for the whole run is the usual reason a parcel ends up at the wrong building.`
                  : 'These are the buildings the driver was delivering to either side of our parcel.'}
              </div>
            </div>
          )}

          <ScanSnapshotPanel snap={c.scan_snapshot}
            title={`${a.for_driver || 'The driver'}’s drops around this parcel`}
            footer="This is the scanner audit for that run — our parcel is the highlighted one, with the deliveries either side of it. If it isn’t at the delivery address, it is almost certainly at one of these." />

          {(c.messages || []).length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {c.messages.map((m) => (
                <div key={m.id} style={{ alignSelf: m.who === 'you' ? 'flex-end' : 'flex-start', maxWidth: '85%', background: m.who === 'you' ? 'rgba(212,175,55,0.12)' : 'var(--hp-black)', border: '1px solid var(--hp-border)', borderRadius: 10, padding: '7px 10px', fontSize: '0.82rem' }}>
                  <div style={{ fontSize: '0.64rem', color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 2 }}>{m.who === 'you' ? 'You' : m.who === 'office' ? 'Office' : (m.driver_login || 'Driver')}</div>
                  {m.outcome && <div style={{ fontWeight: 700 }}>{m.outcome}</div>}
                  {m.message && <div style={{ whiteSpace: 'pre-wrap' }}>{m.message}</div>}
                  {(m.photos || []).map((src, i) => (
                    <a key={i} href={src} target="_blank" rel="noreferrer"><img src={src} alt="" style={{ width: 90, height: 90, objectFit: 'cover', borderRadius: 6, marginTop: 5 }} /></a>
                  ))}
                </div>
              ))}
            </div>
          )}

          <div>
            <div style={{ fontSize: '0.72rem', fontWeight: 800, color: 'var(--hp-gold)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>What happened at the address?</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {OUTCOMES.map((o) => (
                <button key={o.k} onClick={() => setFound(o.k)} style={{ fontSize: '0.78rem', padding: '6px 11px', borderRadius: 16, cursor: 'pointer', border: `1px solid ${found === o.k ? 'var(--hp-gold)' : 'var(--hp-border)'}`, background: found === o.k ? 'rgba(212,175,55,0.15)' : 'transparent', color: 'var(--hp-text)' }}>{o.label}</button>
              ))}
            </div>
          </div>
          <textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Where did you look, who did you speak to, where is the parcel now?"
            style={{ width: '100%', minHeight: 54, background: 'var(--hp-black)', border: '1px solid var(--hp-border)', borderRadius: 8, color: 'var(--hp-text)', padding: 9, fontSize: '0.85rem', fontFamily: 'inherit' }} />
          <CasePhotoField bare photos={photos} onChange={setPhotos} />
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button disabled={busy || (!found && !note && !photos.length)} onClick={() => run({ outcome: outcomeLabel, message: note, done: true })}
              style={{ flex: '1 1 140px', padding: 11, borderRadius: 8, border: 'none', background: 'var(--hp-gold)', color: 'var(--hp-black)', fontWeight: 800, cursor: 'pointer' }}>
              <CheckCircle size={14} /> Report back &amp; finish
            </button>
            <button disabled={busy || (!note && !photos.length)} onClick={() => run({ outcome: outcomeLabel, message: note })}
              style={{ flex: '1 1 120px', padding: 11, borderRadius: 8, border: '1px solid var(--hp-border)', background: 'transparent', color: 'var(--hp-text)', fontWeight: 700, cursor: 'pointer' }}>
              <Send size={14} /> Update, still looking
            </button>
          </div>
          <div style={{ fontSize: '0.74rem', color: 'var(--hp-dim)' }}>
            This is {a.for_driver || 'another driver'}&rsquo;s case — you&rsquo;re only helping recover the parcel. Nothing here counts against your record.
          </div>
          {err && <div style={{ color: '#ef4444', fontSize: '0.8rem' }}>{err}</div>}
        </div>
      )}
    </div>
  );
}

function CustomerCaseCard({ c, onChanged, startOpen, preview }) {
  // Who spoke last. If it was the office, the ball is in the driver's court — the card opens
  // itself and says so. An office reply on a case the driver had already answered is exactly
  // what used to go unseen (the driver answered on WhatsApp instead — Paul, 26 Aug 2026).
  const msgs = c.messages || [];
  const isOpen = (c.status || 'open') === 'open';
  // kind 'admin' is the office's own bookkeeping ("Replied to Australia Post…", "Recovery sent
  // to X") — it lands in the thread but it isn't the office asking the driver anything, so it
  // must not raise the "waiting on you" flag on its own.
  const convo = msgs.filter((m) => !(m.who === 'office' && m.kind === 'admin'));
  const officeWaiting = isOpen && convo.length > 0 && convo[convo.length - 1].who === 'office';
  // officeWaiting overrides even an explicit startOpen={false}: a question from the office
  // that the driver has to scroll and tap to discover is a question that doesn't get answered.
  const [open, setOpen] = useState(officeWaiting || (startOpen != null ? startOpen : (c.needsConfirm || c.stage === 'new')));
  useSeen(c.id, open, preview);
  const [outcome, setOutcome] = useState('');
  const [note, setNote] = useState('');
  const [photos, setPhotos] = useState([]);
  const [activeFollow, setActiveFollow] = useState(null); // a template option whose follow-up is showing
  const [unsure, setUnsure] = useState(false);            // "I'll investigate" → pick expected time
  const [flowAns, setFlowAns] = useState({});             // guided-questionnaire answers by step id
  const [committing, setCommitting] = useState(false);
  const [commitAction, setCommitAction] = useState('');
  const [commitDate, setCommitDate] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const run = async (fn) => {
    if (preview) { setErr(PREVIEW_NOTE); return; }
    setBusy(true); setErr('');
    try {
      const r = await fn();
      if (r && r.success) {
        setNote(''); setPhotos([]); setOutcome(''); setActiveFollow(null); setUnsure(false); setCommitting(false); setCommitAction(''); setCommitDate(''); setFlowAns({});
        onChanged && onChanged();
      } else setErr((r && r.error) || 'Something went wrong');
    } catch { setErr('Network error — try again'); }
    finally { setBusy(false); }
  };
  const send = () => run(() => ccApi('respond', { id: c.id, outcome, message: note, photos: ccPhotoParam(photos) }));
  const resolve = () => run(() => ccApi('respond', { id: c.id, outcome: outcome || 'Sorted', message: note, photos: ccPhotoParam(photos), resolves: true }));
  // A follow-up update on a case that's already been answered — progress, or the end of it.
  const update = () => run(() => ccApi('respond', { id: c.id, message: note, photos: ccPhotoParam(photos) }));
  const sorted = () => run(() => ccApi('respond', { id: c.id, outcome: 'Sorted — parcel with the customer', message: note, photos: ccPhotoParam(photos), resolves: true }));
  const saveCommit = () => run(() => ccApi('commit', { id: c.id, committed_action: commitAction, follow_up_date: commitDate, message: note }));
  const confirmDone = () => run(() => ccApi('confirmDone', { id: c.id, message: note, photos: ccPhotoParam(photos) }));
  const investigate = (kind) => run(() => ccApi('commit', { id: c.id, committed_action: 'Investigating — will report back', follow_up_date: ccExpectedDate(kind), message: note }));

  // Pick a template option: no follow-up → it's the answer; with a follow-up → reveal it.
  const pickTemplate = (opt) => {
    setUnsure(false);
    if (opt.followUp) { setActiveFollow(opt); setOutcome(''); }
    else { setActiveFollow(null); setOutcome(opt.label); }
  };
  const pickFollow = (parent, sub) => { setOutcome(`${parent.label} — ${sub}`); setActiveFollow(null); };
  const templates = ccTemplates(c);

  // Guided questionnaire (delivery-dispute cases). setFlow picks an option; setFlowText fills
  // a revealed prompt; submitFlow composes the answers into one response to the office.
  // Office-built questionnaire (overrides the hardcoded flow when present).
  const hasQuestionnaire = (c.questionnaire || []).length > 0;
  // Answered ≠ finished: if the office added a question after the driver submitted, those come
  // back as a follow-up round instead of a dead "thanks" box.
  const pendingQs = pendingQuestions(c);
  const answeredQ = !!c.questionnaire_answers && pendingQs.length === 0;
  const submitQuestionnaire = (answers) => run(() => ccApi('submitQuestionnaire', { id: c.id, answers, photos: ccPhotoParam(photos) }));
  const flow = ccFlow(c);
  const setFlow = (stepId, opt) => setFlowAns(a => ({ ...a, [stepId]: { k: opt.k, prompt: opt.prompt || '', text: (a[stepId] && a[stepId].text) || '' } }));
  const setFlowText = (stepId, t) => setFlowAns(a => ({ ...a, [stepId]: { ...(a[stepId] || {}), text: t } }));
  const flowReady = !!(flow && flowAns.recall && flowAns.address);
  const submitFlow = () => {
    const bits = [];
    if (flowAns.recall) bits.push(flowAns.recall.k === 'yes' ? 'Recalls the delivery' : 'Does NOT recall the delivery');
    if (flowAns.address) bits.push(flowAns.address.k === 'yes' ? 'Confirms correct address' : `Address NOT correct${flowAns.address.text ? ': ' + flowAns.address.text : ''}`);
    const moreTxt = flowAns.more && flowAns.more.k === 'yes' ? (flowAns.more.text || '') : '';
    const outcomeStr = bits.join(' · ');
    const messageStr = [moreTxt, note].filter(Boolean).join('\n');
    return run(() => ccApi('respond', { id: c.id, outcome: outcomeStr, message: messageStr, photos: ccPhotoParam(photos) }));
  };

  const due = ccDue(c.due_at);
  const answered = c.stage === 'answered';
  // The name recorded on the scan — surfaced for the driver to sanity-check, since a wrong
  // name often triggers these cases. The office decides whether it travels at all now, so
  // there is nothing to infer here: an empty name means it wasn't pushed (or there wasn't one).
  const sigName = (c.signature_name || '').trim();
  // AP records a verbal-authority drop AS the signature name. "Who is VERBAL DROP?" is not a
  // question anyone can answer — ask the one that's actually being disputed.
  const verbalDrop = /verbal/i.test(sigName);
  // AP itself flagged the captured name as not a real person — decided server-side, where
  // the key facts it can be read from stay.
  const signatureInvalid = !!c.signature_invalid;
  // (Removed 24 Aug 2026) There used to be a green "Well done — you recorded this delivered
  // to the Concierge" banner here, inferred by grepping the case text for words like
  // "concierge". It congratulated drivers on drops they had never made: the word came from
  // the CUSTOMER asking for a concierge delivery, not from any scan. Praise that isn't earned
  // is worse than no praise — it tells the driver the case is already settled. Do not re-add
  // an inferred version of this.
  // When the case landed on the driver's portal (admin release / push).
  const arrivedStr = c.released_at ? new Date(c.released_at).toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' }) : '';
  // Where the parcel actually got to, and when. Delivered is the disputed date on most
  // cases — but an onboard scan is NOT a delivery, and calling it one told a driver their
  // parcel was gone when it was still on the van (see utils/caseScan).
  const scan = (() => {
    const fmt = (d) => d.toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
    // Server-derived (always sent — it's which day the case is about), else the scan events
    // if those were pushed, else the pushed snapshot.
    const st = scanState(c.event_timeline);
    const delivered = toDate(c.delivered_at) || (st && st.key === 'delivered' ? st.at : null) || (c.scan_snapshot && toDate(c.scan_snapshot.date));
    if (delivered) return { label: 'Delivered (scanned)', text: fmt(delivered), onboard: false };
    const onboard = toDate(c.onboard_at) || (st && st.key === 'onboard' ? st.at : null);
    if (onboard) return { label: 'Onboard with you', text: fmt(onboard), onboard: true };
    return null;
  })();
  // Once the office has made a fault call, colour the whole card: green = no fault, red = at fault.
  const faultColor = c.fault === 'no' ? 'var(--hp-green)' : c.fault === 'yes' ? '#ef4444' : '';
  const border = faultColor || (c.needsConfirm ? '#f59e0b' : answered ? 'rgba(52,211,153,0.5)' : 'rgba(239,68,68,0.5)');
  // nowrap + no shrink: with five possible chips the row has to wrap to a second line on a
  // phone rather than squash "NOT RECEIVED" into two words stacked on top of each other.
  const chip = { fontSize: '0.66rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.04em', padding: '2px 7px', borderRadius: 5, border: '1px solid', whiteSpace: 'nowrap', flexShrink: 0 };

  // An open case never dead-ends. Whatever stage it's reached, the driver can say something
  // more: progress on an investigation, an answer to a question the office asked after the
  // questionnaire came back, or "I recovered it and handed it to the customer". Until this
  // existed the only route back to the office was WhatsApp (Paul, 26 Aug 2026).
  // withPhoto=false where the surrounding block already owns the photo field — the two would
  // share one `photos` state and fight.
  const replyBox = (withPhoto) => (
    <div style={{ border: `1px solid ${officeWaiting ? 'var(--hp-gold)' : 'var(--hp-border)'}`, background: officeWaiting ? 'rgba(212,175,55,0.07)' : 'transparent', borderRadius: 8, padding: 11, display: 'flex', flexDirection: 'column', gap: 9 }}>
      <div style={{ fontSize: '0.72rem', fontWeight: 800, color: officeWaiting ? 'var(--hp-gold)' : 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'flex', alignItems: 'center', gap: 5 }}>
        <MessageSquare size={12} /> {officeWaiting ? 'The office is waiting on you' : 'Anything more to tell us?'}
      </div>
      <textarea value={note} onChange={(e) => setNote(e.target.value)}
        placeholder="e.g. I found the parcel at reception and handed it to the customer"
        style={{ width: '100%', minHeight: 58, background: 'var(--hp-black)', border: '1px solid var(--hp-border)', borderRadius: 8, color: 'var(--hp-text)', padding: 9, fontSize: '0.85rem', fontFamily: 'inherit', boxSizing: 'border-box' }} />
      {withPhoto && <CasePhotoField requested={c.photo_requested} photos={photos} onChange={setPhotos} />}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button disabled={busy || (!note.trim() && !photos.length)} onClick={update}
          style={{ flex: '1 1 120px', padding: 11, borderRadius: 8, border: 'none', background: 'var(--hp-gold)', color: 'var(--hp-black)', fontWeight: 800, cursor: (note.trim() || photos.length) ? 'pointer' : 'not-allowed', opacity: (note.trim() || photos.length) ? 1 : 0.55 }}><Send size={14} /> Send update</button>
        <button disabled={busy || (!note.trim() && !photos.length)} onClick={sorted}
          style={{ flex: '1 1 120px', padding: 11, borderRadius: 8, border: '1px solid var(--hp-green)', background: 'transparent', color: 'var(--hp-green)', fontWeight: 700, cursor: (note.trim() || photos.length) ? 'pointer' : 'not-allowed', opacity: (note.trim() || photos.length) ? 1 : 0.55 }}><CheckCircle size={14} /> Sorted — parcel delivered</button>
      </div>
      <div style={{ fontSize: '0.72rem', color: 'var(--hp-dim)' }}>
        The office reads everything you put here and it stays on the case — you don&rsquo;t need to text or WhatsApp us.
      </div>
    </div>
  );

  return (
    <div style={{ background: 'var(--hp-surface)', border: `${faultColor ? 2 : 1}px solid ${border}`, borderRadius: 'var(--radius)', padding: 13, marginBottom: 8 }}>
      <div onClick={() => setOpen(o => !o)} style={{ display: 'flex', alignItems: 'center', gap: 8, rowGap: 5, flexWrap: 'wrap', cursor: 'pointer' }}>
        <span style={{ ...chip, color: '#ef4444', borderColor: 'rgba(239,68,68,0.5)' }}>{CC_LABELS[c.category] || 'Enquiry'}</span>
        {c.fault === 'no' && <span style={{ ...chip, color: 'var(--hp-green)', borderColor: 'var(--hp-green)' }}>No fault</span>}
        {c.fault === 'yes' && <span style={{ ...chip, color: '#ef4444', borderColor: '#ef4444' }}>Driver fault</span>}
        {/* A settled case has no clock. It kept counting down in the driver's Resolved list
            and in the leader's view of it — "6h left" on a case the office finished reads
            as work still owed (spotted 1 Sep 2026). */}
        {isOpen && due && due.show && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: '0.7rem', fontWeight: 700, color: due.color, fontFamily: 'var(--font-mono)' }}><Clock size={11} /> {due.label}</span>}
        {answered && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: '0.7rem', color: '#34d399' }}><CheckCircle size={12} /> answered</span>}
        {c.needsConfirm && <span style={{ ...chip, color: '#f59e0b', borderColor: 'rgba(245,158,11,0.6)' }}>Re-attempt due</span>}
        {officeWaiting && <span style={{ ...chip, color: 'var(--hp-gold)', borderColor: 'var(--hp-gold)' }}>Office replied</span>}
        <span style={{ marginLeft: 'auto', color: 'var(--hp-dim)' }}>{open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}</span>
      </div>
      <div style={{ fontSize: '0.9rem', fontWeight: 800, marginTop: 8 }}>{c.case_number ? `Case ${c.case_number}` : 'Customer case'}</div>
      <div style={{ fontSize: '0.8rem', color: 'var(--hp-dim)', marginTop: 3 }}>{c.summary || c.enquiry_sub_type}</div>
      {/* Which parcel, and where it is going. This used to be a fragment of address tacked onto
          the case-number line, with the barcode nowhere on the phone at all — so "stop this one
          and return it to sender" named a parcel the driver had no way to pick out of the van
          (Paul, 4 Sep 2026). Article number is selectable mono text: it gets typed into the
          scanner. */}
      {(c.address || c.tracking_no) && (
        <div style={{ marginTop: 7, padding: '8px 10px', background: 'var(--hp-black)', border: '1px solid var(--hp-border)', borderRadius: 8 }}>
          {c.address && (
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6 }}>
              <MapPin size={13} style={{ color: 'var(--hp-gold)', flexShrink: 0, marginTop: 2 }} />
              <div>
                <div style={{ fontSize: '0.66rem', fontWeight: 800, color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Deliver to</div>
                <div style={{ fontSize: '0.86rem', fontWeight: 700 }}>{c.address}</div>
              </div>
            </div>
          )}
          {c.tracking_no && (
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6, marginTop: c.address ? 7 : 0 }}>
              <Package size={13} style={{ color: 'var(--hp-gold)', flexShrink: 0, marginTop: 2 }} />
              <div>
                <div style={{ fontSize: '0.66rem', fontWeight: 800, color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Article / barcode</div>
                <div style={{ fontSize: '0.82rem', fontWeight: 700, fontFamily: 'var(--font-mono)', userSelect: 'all', wordBreak: 'break-all' }}>{c.tracking_no}</div>
              </div>
            </div>
          )}
        </div>
      )}
      {scan && <div style={{ fontSize: '0.82rem', color: scan.onboard ? 'var(--hp-green)' : '#b45309', fontWeight: 700, marginTop: 5, display: 'flex', alignItems: 'center', gap: 5 }}><Calendar size={13} /> {scan.label} · {scan.text}</div>}
      {arrivedStr && <div style={{ fontSize: '0.72rem', color: 'var(--hp-dim)', marginTop: 3, display: 'flex', alignItems: 'center', gap: 4 }}><Clock size={11} /> Arrived on your portal · {arrivedStr}</div>}

      {open && (
        <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 12 }}>
          {/* The office's note. Nothing appears here unless someone in the office chose to put
              it here during triage — AP's raw customer wording is no longer piped straight
              through, because it regularly confused the driver more than it helped. */}
          {c.driver_note && (
            <div style={{ border: '1px solid rgba(212,175,55,0.45)', background: 'rgba(212,175,55,0.07)', borderRadius: 8, padding: '10px 12px' }}>
              <div style={{ fontSize: '0.68rem', fontWeight: 800, color: 'var(--hp-gold)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 5 }}>Note from the office</div>
              <div style={{ fontSize: '0.84rem', whiteSpace: 'pre-wrap' }}>{c.driver_note}</div>
            </div>
          )}
          <OfficePhotoPanel photos={c.driver_photos} />

          {sigName && (
            <div style={{ border: '2px solid var(--hp-gold)', borderRadius: 8, overflow: 'hidden' }}>
              <div style={{ padding: '10px 12px', background: 'rgba(201,168,76,0.12)' }}>
                <div style={{ fontSize: '0.72rem', fontWeight: 800, color: 'var(--hp-gold)', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'flex', alignItems: 'center', gap: 5, marginBottom: 4 }}><HelpCircle size={13} /> Please confirm the signature</div>
                <div style={{ fontSize: '1.05rem', fontWeight: 800 }}>{verbalDrop ? `The scan says ${sigName} — who told you to leave it, and where?` : `Who is ${sigName}?`}</div>
                {signatureInvalid && (
                  <div style={{ marginTop: 6, display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: '0.72rem', fontWeight: 800, color: '#ef4444', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.5)', borderRadius: 5, padding: '2px 7px' }}>
                    <AlertTriangle size={12} /> Australia Post flagged this name as invalid
                  </div>
                )}
              </div>
              <div style={{ padding: '9px 12px', fontSize: '0.8rem', color: 'var(--hp-dim)' }}>
                {verbalDrop
                  ? 'This is what goes on the scan when nobody signs. Tell us who gave you the go-ahead to leave it and exactly where you left it — that is the whole case.'
                  : 'This is the name captured on the scan. Tell us who they are — reception/concierge, a neighbour, or the customer. A clear, correct name here stops these cases coming back.'}
              </div>
            </div>
          )}

          {(c.images || []).length > 0 && (
            <div>
              <div style={{ fontSize: '0.72rem', fontWeight: 800, color: 'var(--hp-gold)', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'flex', alignItems: 'center', gap: 5, marginBottom: 6 }}><Camera size={13} /> Photo captured at delivery</div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {c.images.map((src, i) => (
                  <a key={i} href={src} target="_blank" rel="noreferrer"><img src={src} alt="delivery" style={{ width: 130, height: 130, objectFit: 'cover', borderRadius: 8, border: '1px solid var(--hp-border)' }} /></a>
                ))}
              </div>
              <div style={{ fontSize: '0.74rem', color: 'var(--hp-dim)', marginTop: 4 }}>Tap to enlarge — does this jog your memory of the drop?</div>
            </div>
          )}

          <ScanSnapshotPanel snap={c.scan_snapshot} title="Your drops around this parcel"
            footer="Do you remember this drop? Tell us below where you left it — anything helps." />

          {(c.event_timeline || []).length > 0 && (
            <details>
              <summary style={{ fontSize: '0.72rem', fontWeight: 800, color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '0.06em', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5 }}><Package size={12} /> Delivery scan events ({c.event_timeline.length})</summary>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 3, marginTop: 6, fontSize: '0.76rem' }}>
                {c.event_timeline.map((e, i) => (
                  <div key={i} style={{ display: 'flex', gap: 6, borderLeft: '2px solid var(--hp-border)', paddingLeft: 7 }}>
                    <span style={{ color: 'var(--hp-dim)', fontFamily: 'var(--font-mono)', whiteSpace: 'nowrap' }}>{e.at || ''}</span>
                    <span>{e.desc}{e.location ? ` · ${e.location}` : ''}</span>
                  </div>
                ))}
              </div>
            </details>
          )}

          {/* (Removed 24 Aug 2026) "The office needs to know" listed c.driver_questions — the
              extractor's auto-drafted questions — immediately above the questionnaire that
              asks the real ones. Two lists of questions, one answerable and one not. The
              questionnaire is the single place the driver is asked anything. */}

          {/* Thread */}
          {c.messages.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {c.messages.map((m) => (
                <div key={m.id} style={{ alignSelf: m.who === 'you' ? 'flex-end' : 'flex-start', maxWidth: '85%', background: m.who === 'you' ? 'rgba(212,175,55,0.12)' : 'var(--hp-black)', border: '1px solid var(--hp-border)', borderRadius: 10, padding: '7px 10px', fontSize: '0.82rem' }}>
                  <div style={{ fontSize: '0.64rem', color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 2 }}>{m.who === 'you' ? 'You' : m.who === 'office' ? 'Office' : (m.driver_login || 'Driver')}</div>
                  {m.outcome && <div style={{ fontWeight: 700 }}>{m.outcome}</div>}
                  {m.message && <div>{m.message}</div>}
                  {(m.photos || []).map((p, i) => <a key={i} href={p} target="_blank" rel="noreferrer"><img src={p} alt="" style={{ width: 60, height: 60, objectFit: 'cover', borderRadius: 6, marginTop: 4, border: '1px solid var(--hp-border)' }} /></a>)}
                </div>
              ))}
            </div>
          )}

          {/* A finished case is a record, not a job. The thread above still reads in full;
              nothing below it would be accepted by the server, so nothing below it is offered. */}
          {!isOpen ? (
            <div style={{ padding: 11, background: 'rgba(52,211,153,0.08)', border: '1px solid rgba(52,211,153,0.4)', borderRadius: 8, fontSize: '0.84rem', display: 'flex', alignItems: 'center', gap: 8 }}>
              <CheckCircle size={15} style={{ color: '#34d399', flexShrink: 0 }} />
              <span>This case is closed. If something has changed on it, tell the office.</span>
            </div>
          ) : /* Carry-over confirm (a committed re-attempt has come due) */ c.needsConfirm ? (
            <div style={{ padding: 12, background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.5)', borderRadius: 8 }}>
              <div style={{ fontWeight: 700, fontSize: '0.9rem', marginBottom: 8 }}>You said you'd: <span style={{ color: '#f59e0b' }}>{c.committed_action}</span></div>
              <div style={{ fontSize: '0.82rem', color: 'var(--hp-dim)', marginBottom: 10 }}>Did you get it done?</div>
              <CasePhotoField requested={c.photo_requested} photos={photos} onChange={setPhotos} />
              <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                <button disabled={busy} onClick={confirmDone} style={{ flex: 1, padding: 12, borderRadius: 8, border: 'none', background: 'var(--hp-green)', color: '#04110b', fontWeight: 800, cursor: 'pointer' }}><CheckCircle size={15} /> Yes, done</button>
                <button disabled={busy} onClick={() => setOpen(false)} style={{ flex: 1, padding: 12, borderRadius: 8, border: '1px solid var(--hp-border)', background: 'transparent', color: 'var(--hp-text)', fontWeight: 700, cursor: 'pointer' }}>Not yet</button>
              </div>
              {/* Not yet ≠ nothing to say — the driver can report what they found without
                  claiming the re-attempt is done. */}
              <div style={{ marginTop: 10 }}>{replyBox(false)}</div>
            </div>
          ) : hasQuestionnaire ? (
            answeredQ ? (
              <>
                <div style={{ padding: 12, background: 'rgba(52,211,153,0.08)', border: '1px solid rgba(52,211,153,0.5)', borderRadius: 8, fontSize: '0.86rem', display: 'flex', alignItems: 'center', gap: 8 }}>
                  <CheckCircle size={16} style={{ color: '#34d399' }} /> Thanks — your answers are with the office.
                </div>
                {replyBox(true)}
              </>
            ) : (
              <CaseQuestionnaire c={c} busy={busy} onSubmit={submitQuestionnaire}
                extra={<CasePhotoField requested={c.photo_requested} photos={photos} onChange={setPhotos} />} />
            )
          ) : (
            <>
              {flow ? (
                /* Guided questionnaire for delivery-dispute cases */
                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                  {flow.map(step => {
                    const ans = flowAns[step.id];
                    return (
                      <div key={step.id}>
                        <div style={{ fontSize: '0.88rem', fontWeight: 700, marginBottom: 7 }}>{step.q}</div>
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                          {step.opts.map(opt => (
                            <button key={opt.k} onClick={() => setFlow(step.id, opt)} style={{ fontSize: '0.82rem', padding: '8px 18px', borderRadius: 8, cursor: 'pointer', fontWeight: 700, border: `1px solid ${ans && ans.k === opt.k ? 'var(--hp-gold)' : 'var(--hp-border)'}`, background: ans && ans.k === opt.k ? 'rgba(212,175,55,0.15)' : 'transparent', color: 'var(--hp-text)' }}>{opt.label}</button>
                          ))}
                        </div>
                        {ans && ans.prompt && (
                          <textarea value={ans.text} onChange={e => setFlowText(step.id, e.target.value)} placeholder={ans.prompt} style={{ width: '100%', minHeight: 46, marginTop: 8, background: 'var(--hp-black)', border: '1px solid var(--hp-border)', borderRadius: 8, color: 'var(--hp-text)', padding: 9, fontSize: '0.85rem', fontFamily: 'inherit', boxSizing: 'border-box' }} />
                        )}
                      </div>
                    );
                  })}
                  <CasePhotoField requested={c.photo_requested} photos={photos} onChange={setPhotos} />
                  <button disabled={busy || !flowReady} onClick={submitFlow} style={{ padding: 12, borderRadius: 8, border: 'none', background: 'var(--hp-gold)', color: 'var(--hp-black)', fontWeight: 800, cursor: flowReady ? 'pointer' : 'not-allowed', opacity: flowReady ? 1 : 0.55 }}><Send size={14} /> Send to office</button>
                </div>
              ) : (
                <>
                  {/* Quick-answer chips for the simpler case types */}
                  {(templates.length > 0 || c.quick_answers.length > 0) && (
                    <div>
                      <div style={{ fontSize: '0.72rem', fontWeight: 800, color: 'var(--hp-gold)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>Pick what happened</div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                        {templates.map((opt, i) => (
                          <button key={i} onClick={() => pickTemplate(opt)} style={{ fontSize: '0.78rem', padding: '6px 11px', borderRadius: 16, cursor: 'pointer', border: `1px solid ${(outcome === opt.label || outcome.startsWith(opt.label + ' —') || (activeFollow && activeFollow.label === opt.label)) ? 'var(--hp-gold)' : 'var(--hp-border)'}`, background: (outcome === opt.label || outcome.startsWith(opt.label + ' —') || (activeFollow && activeFollow.label === opt.label)) ? 'rgba(212,175,55,0.15)' : 'transparent', color: 'var(--hp-text)' }}>{opt.label}{opt.followUp ? ' …' : ''}</button>
                        ))}
                        {c.quick_answers.filter(a => !templates.some(t => t.label === a)).map((a, i) => (
                          <button key={'q' + i} onClick={() => { setUnsure(false); setActiveFollow(null); setOutcome(a); }} style={{ fontSize: '0.78rem', padding: '6px 11px', borderRadius: 16, cursor: 'pointer', border: `1px solid ${outcome === a ? 'var(--hp-gold)' : 'var(--hp-border)'}`, background: outcome === a ? 'rgba(212,175,55,0.15)' : 'transparent', color: 'var(--hp-text)' }}>{a}</button>
                        ))}
                      </div>
                      {activeFollow && (
                        <div style={{ marginTop: 8, padding: 10, border: '1px solid var(--hp-gold)', borderRadius: 8 }}>
                          <div style={{ fontSize: '0.82rem', fontWeight: 700, marginBottom: 6 }}>{activeFollow.followUp.question}</div>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                            {activeFollow.followUp.options.map((sub, i) => (
                              <button key={i} onClick={() => pickFollow(activeFollow, sub)} style={{ fontSize: '0.78rem', padding: '6px 11px', borderRadius: 16, cursor: 'pointer', border: '1px solid var(--hp-border)', background: 'transparent', color: 'var(--hp-text)' }}>{sub}</button>
                            ))}
                          </div>
                        </div>
                      )}
                      {outcome && !activeFollow && <div style={{ fontSize: '0.78rem', color: '#34d399', marginTop: 6 }}>Selected: {outcome}</div>}
                    </div>
                  )}
                  <textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add detail or reply to the office…" style={{ width: '100%', minHeight: 54, background: 'var(--hp-black)', border: '1px solid var(--hp-border)', borderRadius: 8, color: 'var(--hp-text)', padding: 9, fontSize: '0.85rem', fontFamily: 'inherit' }} />
                  <CasePhotoField requested={c.photo_requested} photos={photos} onChange={setPhotos} />
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <button disabled={busy || (!outcome && !note && !photos.length)} onClick={send} style={{ flex: '1 1 120px', padding: 11, borderRadius: 8, border: 'none', background: 'var(--hp-gold)', color: 'var(--hp-black)', fontWeight: 800, cursor: 'pointer' }}><Send size={14} /> Send answer</button>
                    <button disabled={busy} onClick={resolve} style={{ flex: '1 1 120px', padding: 11, borderRadius: 8, border: '1px solid var(--hp-green)', background: 'transparent', color: 'var(--hp-green)', fontWeight: 700, cursor: 'pointer' }}><CheckCircle size={14} /> This is sorted</button>
                  </div>
                </>
              )}
              {/* Unsure → investigate, with an expected-response time back to the office */}
              {!unsure ? (
                <button onClick={() => { setUnsure(true); setOutcome(''); setActiveFollow(null); }} style={{ alignSelf: 'flex-start', background: 'none', border: 'none', color: 'var(--hp-dim)', fontSize: '0.8rem', textDecoration: 'underline', cursor: 'pointer', padding: 0 }}>I'm unsure — I'll investigate and report back</button>
              ) : (
                <div style={{ padding: 10, border: '1px solid var(--hp-border)', borderRadius: 8 }}>
                  <div style={{ fontSize: '0.82rem', fontWeight: 700, marginBottom: 8 }}>When will you have an answer?</div>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {[['same', 'Same day'], ['next', 'Next day'], ['two', 'Within 2 days']].map(([k, lbl]) => (
                      <button key={k} disabled={busy} onClick={() => investigate(k)} style={{ fontSize: '0.8rem', padding: '8px 12px', borderRadius: 8, cursor: 'pointer', border: '1px solid var(--hp-gold)', background: 'rgba(212,175,55,0.1)', color: 'var(--hp-text)', fontWeight: 700 }}>{lbl}</button>
                    ))}
                    <button disabled={busy} onClick={() => setUnsure(false)} style={{ fontSize: '0.8rem', padding: '8px 12px', borderRadius: 8, cursor: 'pointer', border: '1px solid var(--hp-border)', background: 'transparent', color: 'var(--hp-dim)' }}>Cancel</button>
                  </div>
                </div>
              )}
              {!flow && (!committing ? (
                <button onClick={() => setCommitting(true)} style={{ alignSelf: 'flex-start', background: 'none', border: 'none', color: 'var(--hp-dim)', fontSize: '0.8rem', textDecoration: 'underline', cursor: 'pointer', padding: 0 }}>Can't do it now — I'll re-attempt later</button>
              ) : (
                <div style={{ padding: 10, border: '1px solid var(--hp-border)', borderRadius: 8, display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <input value={commitAction} onChange={(e) => setCommitAction(e.target.value)} placeholder="What you'll do (e.g. Re-attempt at #5)" style={{ background: 'var(--hp-black)', border: '1px solid var(--hp-border)', borderRadius: 8, color: 'var(--hp-text)', padding: 9, fontSize: '0.85rem' }} />
                  <label style={{ fontSize: '0.74rem', color: 'var(--hp-dim)' }}>When?</label>
                  <input type="date" value={commitDate} onChange={(e) => setCommitDate(e.target.value)} style={{ background: 'var(--hp-black)', border: '1px solid var(--hp-border)', borderRadius: 8, color: 'var(--hp-text)', padding: 9, fontSize: '0.85rem' }} />
                  <button disabled={busy || !commitAction} onClick={saveCommit} style={{ padding: 10, borderRadius: 8, border: 'none', background: 'var(--hp-gold)', color: 'var(--hp-black)', fontWeight: 800, cursor: 'pointer' }}>Save — remind me on my next shift</button>
                </div>
              ))}
            </>
          )}
          {err && <div style={{ color: '#ef4444', fontSize: '0.8rem' }}>{err}</div>}
        </div>
      )}
    </div>
  );
}


export { previewVerdict, CustomerCaseCard, AssistCaseCard, DeliveryRequestCard, CasePhotoField, ScanSnapshotPanel, ccApi, ccPhotoParam, ccDue, ampm, fmtDay, CC_LABELS, likelyBuildings };
export default CustomerCaseCard;
